import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { STATUS_LABEL, STATUS_BADGE, PAYMENT_BADGE } from "@/lib/statusLabels";
import { FileText, CheckCircle2, AlertTriangle, Wallet, ArrowRight, Bell } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, n] = await Promise.all([api.get("/orders/mine"), api.get("/notifications")]);
        setOrders(o.data.orders);
        setNotifs(n.data.notifications);
      } catch {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activeStatuses = new Set(["payment_confirmed", "documents_pending", "documents_received", "under_review", "processing", "submitted"]);
  const active = orders.filter((o) => activeStatuses.has(o.current_status)).length;
  const completed = orders.filter((o) => o.current_status === "completed").length;
  const pendingDocs = orders.filter((o) => o.current_status === "documents_pending").length;

  const stats = [
    { label: "Active Filings", value: active, icon: FileText, tint: "bg-blue-50 text-[#1E3A8A]" },
    { label: "Completed Orders", value: completed, icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-700" },
    { label: "Pending Documents", value: pendingDocs, icon: AlertTriangle, tint: "bg-amber-50 text-amber-700" },
    { label: "Total Orders", value: orders.length, icon: Wallet, tint: "bg-slate-100 text-slate-700" },
  ];

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="section-label">Dashboard</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]" data-testid="customer-welcome">
            Welcome, {user?.full_name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Here's your filing overview.</p>
        </div>
        <Link to="/services">
          <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="dash-new-filing-btn">
            Start a new filing <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="card-crisp rounded-xl p-5" data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.tint}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-4 text-3xl font-display font-bold text-[#0A192F]">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white">
          <div className="p-5 border-b border-slate-200 flex justify-between items-center">
            <div>
              <div className="section-label">Active Filings</div>
              <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Your recent orders</div>
            </div>
            <Link to="/dashboard/orders" className="text-sm text-[#1E3A8A] hover:underline">View all →</Link>
          </div>
          {loading ? (
            <div className="p-6 text-slate-500 text-sm">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="p-10 text-center">
              <FileText className="w-8 h-8 mx-auto text-slate-400" />
              <div className="mt-3 font-medium text-slate-700">No filings yet</div>
              <div className="text-sm text-slate-500 mt-1">Start your first filing to see it here.</div>
              <Link to="/services"><Button className="mt-4 bg-[#1E3A8A]" data-testid="empty-cta-services">Browse services</Button></Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {orders.slice(0, 6).map((o) => (
                <button
                  key={o.id}
                  className="w-full text-left p-5 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4"
                  onClick={() => nav(`/dashboard/orders/${o.id}`)}
                  data-testid={`order-row-${o.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-[#0A192F] text-sm">{o.order_number || "—"}</span>
                      <span className={`status-badge ${PAYMENT_BADGE[o.payment_status] || "badge-muted"}`}>{o.payment_status}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-700 truncate">{o.service_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`status-badge ${STATUS_BADGE[o.current_status] || "badge-muted"}`}>
                      {STATUS_LABEL[o.current_status] || o.current_status}
                    </span>
                    <div className="text-xs text-slate-500">₹{o.amount}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="p-5 border-b border-slate-200">
            <div className="section-label">Notifications</div>
            <div className="font-display font-semibold text-lg text-[#0A192F] mt-1">Latest updates</div>
          </div>
          {notifs.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <Bell className="w-6 h-6 mx-auto text-slate-400 mb-2" />
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
              {notifs.slice(0, 10).map((n) => (
                <li key={n.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 mt-1.5 rounded-full ${n.read ? "bg-slate-300" : "bg-[#1E3A8A]"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A192F]">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                        {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
